/**
 * TranscriptPanel — Interactive transcript sidebar for video scenes.
 *
 * Features:
 *   - Scrolling text synced to video playback (highlights current segment)
 *   - Click any line to seek the video to that timestamp
 *   - Each line shows timestamp + text
 *   - Collapse/expand toggle
 *   - Search within transcript
 *
 * Props:
 *   segments       — Array of {start_sec, end_sec, text}
 *   currentTime    — Current video playback time in seconds
 *   onSeek         — Callback(seconds) to seek the video
 *   defaultExpanded — Whether the panel starts expanded (default: true)
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';

function formatTimestamp(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function TranscriptPanel({ segments, currentTime = 0, onSeek, defaultExpanded = true }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [searchQuery, setSearchQuery] = useState('');
  const activeRef = useRef(null);
  const containerRef = useRef(null);
  const userScrolledRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  const activeIndex = segments.findIndex(
    (seg) => currentTime >= seg.start_sec && currentTime < seg.end_sec
  );

  const filteredSegments = searchQuery.trim()
    ? segments.filter((seg) =>
        seg.text.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : segments;

  const handleScroll = useCallback(() => {
    userScrolledRef.current = true;
    clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      userScrolledRef.current = false;
    }, 3000);
  }, []);

  useEffect(() => {
    if (!expanded || userScrolledRef.current || !activeRef.current) return;
    activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeIndex, expanded]);

  useEffect(() => {
    return () => clearTimeout(scrollTimeoutRef.current);
  }, []);

  if (!segments || segments.length === 0) return null;

  return (
    <div style={panelStyle}>
      <button
        onClick={() => setExpanded((v) => !v)}
        style={toggleBtnStyle}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse transcript' : 'Expand transcript'}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
            <rect x="1" y="3" width="14" height="2" rx="1" fill="currentColor" />
            <rect x="1" y="7" width="10" height="2" rx="1" fill="currentColor" />
            <rect x="1" y="11" width="12" height="2" rx="1" fill="currentColor" />
          </svg>
          Transcript
        </span>
        <span style={{ fontSize: 12, color: '#64748b' }}>
          {expanded ? '▾' : '▸'}
        </span>
      </button>

      {expanded && (
        <>
          <div style={searchWrapStyle}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search transcript..."
              style={searchInputStyle}
              aria-label="Search transcript"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={clearBtnStyle}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div
            ref={containerRef}
            onScroll={handleScroll}
            style={segmentListStyle}
          >
            {filteredSegments.length === 0 && (
              <div style={{ padding: '12px 14px', color: '#94a3b8', fontSize: 13 }}>
                No results for "{searchQuery}"
              </div>
            )}
            {filteredSegments.map((seg, i) => {
              const originalIndex = segments.indexOf(seg);
              const isActive = originalIndex === activeIndex && !searchQuery;
              return (
                <button
                  key={i}
                  ref={isActive ? activeRef : null}
                  onClick={() => onSeek && onSeek(seg.start_sec)}
                  style={{
                    ...segmentBtnStyle,
                    background: isActive ? '#eff6ff' : 'transparent',
                    borderLeft: isActive ? '3px solid #3b82f6' : '3px solid transparent',
                  }}
                  title={`Seek to ${formatTimestamp(seg.start_sec)}`}
                >
                  <span style={timestampStyle}>
                    {formatTimestamp(seg.start_sec)}
                  </span>
                  <span style={{
                    ...textStyle,
                    color: isActive ? '#1e40af' : '#374151',
                    fontWeight: isActive ? 600 : 400,
                  }}>
                    {searchQuery ? highlightMatch(seg.text, searchQuery) : seg.text}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function highlightMatch(text, query) {
  if (!query) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} style={{ background: '#fef08a', borderRadius: 2, padding: '0 1px' }}>
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const panelStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  overflow: 'hidden',
  background: '#fff',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const toggleBtnStyle = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 14px',
  background: '#f8fafc',
  border: 'none',
  borderBottom: '1px solid #e2e8f0',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  color: '#1e293b',
};

const searchWrapStyle = {
  position: 'relative',
  padding: '8px 12px',
  borderBottom: '1px solid #f1f5f9',
};

const searchInputStyle = {
  width: '100%',
  padding: '6px 28px 6px 10px',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
  background: '#f8fafc',
};

const clearBtnStyle = {
  position: 'absolute',
  right: 18,
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 12,
  color: '#94a3b8',
  padding: 4,
};

const segmentListStyle = {
  maxHeight: 320,
  overflowY: 'auto',
};

const segmentBtnStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  width: '100%',
  padding: '8px 12px',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontSize: 'inherit',
  transition: 'background 0.1s',
};

const timestampStyle = {
  fontSize: 11,
  color: '#64748b',
  fontFamily: 'monospace',
  flexShrink: 0,
  marginTop: 2,
  minWidth: 36,
};

const textStyle = {
  fontSize: 13,
  lineHeight: 1.5,
};
